'use client'
import { useSettingsContext } from '@/contexts/settings/provider'
import {
  createBreakpoints,
  createComponentStyles,
  createPaletteConfig,
  createTypographyConfig,
} from '@/theme/utils'
import CssBaseline from '@mui/material/CssBaseline'
import { createTheme, Theme, ThemeProvider as ThemeVarsProvider } from '@mui/material/styles'
import { ThemeProvider as NextThemesProvider } from 'next-themes'
import { Geologica, Inter, Oswald } from 'next/font/google'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/vi'
import { mixins } from './mixins'
const inter = Inter({
  weight: ['300', '400', '500', '600', '700', '800', '900'],
  subsets: ['latin'],
  display: 'swap',
})
const oswald = Oswald({
  weight: ['400', '500', '600', '700'],
  subsets: ['latin'],
  display: 'swap',
})
const geologica = Geologica({
  weight: ['300', '400', '500', '600', '700', '800', '900'],
  subsets: ['latin'],
  display: 'swap',
})

export function ThemeProvider({ children, ...other }: { children: React.ReactNode; other?: any }) {
  const { values } = useSettingsContext()

  // Destructure settings values with defaults
  const {
    mode = 'light',
    fontSize = '16px',
    theme: { colors: themeColors },
  } = values

  const themeMode = mode as 'light' | 'dark'
  const baseFontSize = parseInt(fontSize) || 16
  const theme: Theme = createTheme({
    colorSchemes: {
      light: themeMode === 'light',
      dark: themeMode === 'dark',
    },

    cssVariables: true,
    typography: createTypographyConfig(
      [geologica.style.fontFamily, inter.style.fontFamily, oswald.style.fontFamily].join(','),
      baseFontSize
    ),
    palette: createPaletteConfig(themeColors, themeMode),
    components: createComponentStyles(themeColors, themeMode),
    breakpoints: createBreakpoints(),
    mixins: mixins,
  })

  return (
    <NextThemesProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="theme-mode">
      <ThemeVarsProvider theme={theme} {...other}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
          <CssBaseline />
          {children}
        </LocalizationProvider>
      </ThemeVarsProvider>
    </NextThemesProvider>
  )
}
